import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useDesignDocumentDetail } from './hooks/useDesignDocuments';
import { Button, Badge, Card, FeaturePlaceholder, Alert, KPICard, Table, Avatar } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  submitted: 'bg-blue-50 text-blue-500',
  under_review: 'bg-amber-100 text-amber-600',
  approved: 'bg-emerald-100 text-emerald-600',
  approved_with_comments: 'bg-indigo-100 text-indigo-600',
  rejected: 'bg-red-100 text-red-600',
  superseded: 'bg-slate-400 text-white',
  obsolete: 'bg-slate-900 text-white',
  for_construction: 'bg-os-primary text-white',
  as_built: 'bg-teal-600 text-white',
};

const statusLabels: Record<string, string> = {
  draft: 'Bản thảo',
  submitted: 'Đã nộp',
  under_review: 'Đang soát xét',
  approved: 'Đã duyệt',
  approved_with_comments: 'Duyệt kèm ý kiến',
  rejected: 'Từ chối',
  superseded: 'Đã thay thế',
  obsolete: 'Hết hiệu lực',
  for_construction: 'Dùng thi công',
  as_built: 'Hoàn công',
};

const typeLabels: Record<string, string> = {
  landscape_master_plan: 'Tổng mặt bằng',
  planting_plan: 'Bản vẽ Cây xanh',
  irrigation_plan: 'Bản vẽ Tưới',
  lighting_plan: 'Bản vẽ Đèn',
  hardscape_plan: 'Bản vẽ Hardscape',
  grading_plan: 'Bản vẽ San nền',
  shop_drawing: 'Shop Drawing',
  as_built_drawing: 'Bản vẽ Hoàn công',
  specification: 'Spec / Quy cách',
  method_statement: 'Biện pháp thi công',
  material_approval: 'Phê duyệt vật liệu',
  sample_approval: 'Phê duyệt mẫu',
  rfi_response: 'Phản hồi RFI',
  design_change: 'Thay đổi thiết kế',
};

export default function DesignDocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const { document: item, isLoading, updateDocument } = useDesignDocumentDetail(id);

  const [activeTab, setActiveTab] = useState('overview');

  if (isLoading) return <div className="p-12 text-center text-slate-500 sm:text-sm text-xs">Đang tải chi tiết hồ sơ...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy thông tin hồ sơ thiết kế</div>;

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updateDocument({ status_code: newStatus as any });
      toast.success(`Đã cập nhật trạng thái hồ sơ sang ${statusLabels[newStatus]}`);
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'versions', label: 'Lịch sử Phiên bản' },
    { value: 'links', label: 'Liên kết BOQ / Zone' },
    { value: 'reviews', label: 'Soát xét & Ý kiến' },
    { value: 'audit', label: 'Nhật ký hệ thống' },
  ];

  return (
    <PageDetailLayout
      title={item.title}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${statusColors[item.status_code] || 'bg-slate-100 text-slate-600'} border-transparent text-[10px] font-bold`}>
            {statusLabels[item.status_code] || item.status_code}
          </Badge>
          <span className="sm:text-sm text-xs text-slate-400 font-mono">#{item.document_code}</span>
          <span className="text-slate-300">|</span>
          <span className="sm:text-sm text-xs text-slate-500 flex items-center gap-1">
            <i className="ri-building-line" />
            {item.project?.name}
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Hồ sơ thiết kế', path: '/projects/design-documents' },
        { label: item.document_code }
      ]}
      headerActions={
        <div className="flex gap-2">
          {item.status_code === 'under_review' && (
            <>
              <AuthButton module="projects" action="update" variant="success" icon="ri-check-line" onClick={() => handleStatusChange('approved')}>
                Phê duyệt
              </AuthButton>
              <AuthButton module="projects" action="update" variant="danger" icon="ri-close-line" onClick={() => handleStatusChange('rejected')}>
                Từ chối
              </AuthButton>
            </>
          )}
          {item.status_code === 'approved' && (
            <AuthButton module="projects" action="update" variant="primary" icon="ri-tools-line" onClick={() => handleStatusChange('for_construction')}>
              Phát hành Thi công (IFC)
            </AuthButton>
          )}
          <Button variant="outline" icon="ri-download-cloud-2-line">Tải tài liệu</Button>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard title="Phiên bản hiện tại" value={`R${item.revision_no}`} icon="ri-git-branch-line" variant="primary" />
                <KPICard title="Phân loại hồ sơ" value={typeLabels[item.document_type]} icon="ri-file-settings-line" variant="info" />
                <KPICard title="Lượt soát xét" value={2} icon="ri-eye-line" variant="warning" />
              </div>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-information-line text-os-primary" />
                  Mô tả hồ sơ
                </h3>
                <p className="sm:text-sm text-xs text-slate-600 leading-relaxed bg-slate-50 p-4 rounded-2xl border border-slate-100 italic">
                  "{item.notes || 'Chưa có ghi chú chi tiết cho hồ sơ này.'}"
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12 mt-6">
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="sm:text-sm text-xs text-slate-500">Định vị khu vực</span>
                    <span className="sm:text-sm text-xs font-normal text-os-primary underline">{item.zone?.name || 'Toàn dự án'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="sm:text-sm text-xs text-slate-500">Giai đoạn liên quan</span>
                    <span className="sm:text-sm text-xs font-normal text-slate-700">{item.phase?.name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="sm:text-sm text-xs text-slate-500">Người nộp hồ sơ</span>
                    <span className="sm:text-sm text-xs font-normal text-slate-700">{item.uploader?.full_name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="sm:text-sm text-xs text-slate-500">Ngày nộp</span>
                    <span className="sm:text-sm text-xs font-normal text-slate-700">{item.submitted_at ? new Date(item.submitted_at).toLocaleDateString('vi-VN') : '---'}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-0 overflow-hidden border-2 border-slate-100 bg-slate-50 min-h-[400px] flex items-center justify-center">
                <div className="text-center">
                  <i className="ri-image-2-line text-6xl text-slate-200 mb-4 block" />
                  <p className="sm:text-sm text-xs font-normal text-slate-400">Xem trước bản vẽ (Document Viewer)</p>
                  <Button variant="ghost" size="sm" className="mt-4 text-os-primary font-bold tracking-widest">Mở trình xem nâng cao</Button>
                </div>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="p-6 bg-slate-900 text-white">
                <h4 className="font-bold mb-4 flex items-center gap-2 text-os-primary">
                  <i className="ri-shield-check-line" />
                  Kiểm soát Tài liệu
                </h4>
                <div className="space-y-4">
                  <div className="p-3 bg-slate-800 rounded-2xl border border-slate-700">
                    <p className="text-[10px] text-slate-400 font-bold mb-1">Mục đích sử dụng</p>
                    <p className={`sm:text-sm text-xs font-normal ${item.status_code === 'for_construction' ? 'text-emerald-400' : 'text-amber-400'} italic tracking-tighter`}>
                      {item.status_code === 'for_construction' ? 'ĐƯỢC PHÉP THI CÔNG' : 'CHỈ DÙNG ĐỂ SOÁT XÉT'}
                    </p>
                  </div>
                  <div className="p-3 bg-slate-800 rounded-2xl border border-slate-700">
                    <p className="text-[10px] text-slate-400 font-bold mb-1">Hiệu lực từ</p>
                    <p className="sm:text-sm text-xs font-normal text-slate-300">{item.effective_from ? new Date(item.effective_from).toLocaleDateString('vi-VN') : '---'}</p>
                  </div>
                </div>
                {item.status_code === 'superseded' && (
                  <div className="mt-6 pt-4 border-t border-slate-800">
                    <Alert variant="danger" className="bg-red-900/30 border-red-800 text-red-200 sm:text-sm text-xs">
                      Bản vẽ này đã bị thay thế bởi phiên bản mới hơn.
                    </Alert>
                  </div>
                )}
              </Card>

              <Card className="p-6">
                <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-discuss-line text-slate-400" />
                  Ý kiến Soát xét
                </h4>
                <div className="space-y-4">
                  <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                    <div className="flex items-center gap-2 mb-1">
                      <Avatar size="xs" name="Architect" />
                      <span className="text-[10px] font-bold text-slate-700">Architect Manager</span>
                    </div>
                    <p className="sm:text-sm text-xs text-slate-600 italic">"Cần kiểm tra lại cốt cao độ tại khu vực lối vào A1..."</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="w-full mt-6 text-os-primary sm:text-sm text-xs font-normal">Thêm ý kiến</Button>
              </Card>
            </div>
          </div>
        )}

        {activeTab !== 'overview' && (
          <FeaturePlaceholder
            title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
            description="Dữ liệu đang được liên kết từ các phân hệ Quản lý phiên bản, Liên kết BOQ và Nhật ký soát xét hồ sơ."
          />
        )}
      </div>
    </PageDetailLayout>
  );
}
